import { ref, onMounted, onUnmounted } from "vue"
import * as Sentry from "@sentry/vue"
import { format, formatDistanceToNow, transpose, isAfter, subHours } from "date-fns"
import { TZDate, tz } from "@date-fns/tz"

// Usage info:
// -----
// Keeps a pending count of api/crowdinapi calls reactive without causing infinite loops of reactivity

// Track whether the session has been verified with the server in this page load.
// Resets on every fresh tab open (since in-memory state is lost).
let sessionVerifiedThisLoad = false
export function setSessionVerified(value: boolean) {
  sessionVerifiedThisLoad = value
}

let _pending = { db: 0, crowdin: 0 }

function updatePending(type: "db" | "crowdin", delta: number) {
  _pending[type] += delta
  pendingFetches.value = { ..._pending } // triggers only when you intentionally update it
}

export const pendingFetches = ref({ ..._pending })

// Get the session token from localStorage (works even when the cookie is httponly)
export function getSessionToken() {
  try {
    const session = localStorage.getItem("babble_session")
    if (session) return JSON.parse(session).token || null
  } catch {}
  return null
}

// Dedup parallel getUser calls — only one verifySession in-flight at a time
let getUserPromise: Promise<any> | null = null

// Dedup parallel CSRF refresh calls — only one verifySession in-flight at a time
let csrfRefreshPromise: Promise<any> | null = null

async function refreshCsrf() {
  if (csrfRefreshPromise) return csrfRefreshPromise
  csrfRefreshPromise = authApi("verifySession", {}).finally(() => {
    csrfRefreshPromise = null
  })
  return csrfRefreshPromise
}

// Persistent auth log — survives page redirects. Check via: JSON.parse(localStorage.getItem("babble/authlog"))
function authLog(event: string, details: Record<string, any> = {}) {
  try {
    const log = JSON.parse(localStorage.getItem("babble/authlog") || "[]")
    log.push({ time: new Date().toISOString(), event, ...details })
    // Keep last 50 entries
    if (log.length > 50) log.splice(0, log.length - 50)
    localStorage.setItem("babble/authlog", JSON.stringify(log))
  } catch {}
}

// Shared auth error handling for api() and crowdinapi()
async function handleAuthError(response: Response, caller: string, endpoint: string, retryFn: (() => any) | null): Promise<any> {
  if (response.status === 401) {
    const error = await response.json().catch(() => ({}))
    authLog("401", { caller, endpoint, error: error.error, hadSession: !!localStorage.getItem("babble_session") })

    // Before destroying the session, verify with auth.php directly.
    // A background poll might get a 401 from vue.php (e.g. transient issue,
    // server restart) while the session is still valid in the DB.
    // Uses dedup so parallel 401s share a single verifySession call.
    const hadSession = !!localStorage.getItem("babble_session")
    if (hadSession) {
      const verify = await refreshCsrf().catch(() => ({ valid: false }))
      if (verify.valid) {
        // Session is still good — refresh localStorage and CSRF, don't logout
        authLog("401-recovered", { caller, endpoint, message: "session still valid, transient 401" })
        localStorage.setItem("babble/csrf", verify.user.csrf)
        localStorage.setItem("babble_session", JSON.stringify({ token: verify.sessionId, data: verify.user }))
        sessionVerifiedThisLoad = true
        // Retry the original request if possible
        if (retryFn) return retryFn()
        return { error: error.error || "Session expired" }
      }
      authLog("401-logout", { caller, endpoint, verifyResult: verify.reason || "invalid", message: "session confirmed gone" })
    }

    localStorage.removeItem("babble_session")
    localStorage.removeItem("babble/csrf")

    if (hadSession && !window.location.pathname.includes("dashboard/login")) {
      const vuePath = window.location.pathname.replace(/^\/dashboard/, "") || "/"
      const returnUrl = encodeURIComponent(vuePath + window.location.search)
      authLog("redirect-login", { caller, endpoint, returnUrl })
      window.location.href = `/dashboard/login?to=${returnUrl}`
    }
    return { error: error.error || "Session expired" }
  }

  if (response.status === 403) {
    const error = await response.json().catch(() => ({}))
    authLog("403", { caller, endpoint, error: error.error })

    if (error.error?.includes("CSRF") && retryFn) {
      // Call verifySession with no sessionId so auth.php uses the httponly
      // BABBLE_SID cookie — the same session vue.php validates against.
      // Uses dedup so parallel 403s share a single verifySession call.
      const result = await refreshCsrf()
      if (result.valid) {
        authLog("403-recovered", { caller, endpoint, message: "CSRF refreshed" })
        localStorage.setItem("babble/csrf", result.user.csrf)
        localStorage.setItem("babble_session", JSON.stringify({ token: result.sessionId, data: result.user }))
        sessionVerifiedThisLoad = true
        return retryFn()
      }
      authLog("403-failed", { caller, endpoint, verifyResult: result.reason || "invalid" })
    }
    return { error: error.error || "Forbidden" }
  }

  return null // no auth error
}

export async function api(endpoint: string, method: string, params: any = {}, offset: number = 0, abortSignal: AbortSignal | null = null) {
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  const requestOptions: RequestInit = { method: method, headers: headers, signal: abortSignal }
  const url = new URL(`${import.meta.env.VITE_API_URL}vue.php`)
  switch (method) {
    case "GET":
      params = { request: endpoint, offset: offset }
      url.search = new URLSearchParams(params).toString()
      // Send the session cookie so GET endpoints can authenticate server-side
      // (the dispatcher now requires a valid session for non-public endpoints).
      requestOptions.credentials = "include"
      break
    case "POST":
      headers.set("X-CSRF-Token", localStorage.getItem("babble/csrf") || "")
      headers.set("X-SESSION-TOKEN", getSessionToken() || "")
      url.search = new URLSearchParams({ request: endpoint, offset: String(offset) }).toString()
      requestOptions.body = JSON.stringify(params)
      requestOptions.credentials = "include"
      break
  }
  let response: Response | null = null
  let raw = ""
  try {
    updatePending("db", 1)
    response = await fetch(url, requestOptions)

    const retryFn = !params._csrfRetry ? () => api(endpoint, method, { ...params, _csrfRetry: true }, offset, abortSignal) : null
    const authError = await handleAuthError(response, "api", endpoint, retryFn)
    if (authError) return authError

    raw = await response.text()
    try {
      const answer = JSON.parse(raw)
      if (answer?.csrf) localStorage.setItem("babble/csrf", answer.csrf)
      return answer
    } catch {
      return nonJsonResponseError("api", endpoint, response, raw)
    }
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.warn(`[api] Fetch error on ${endpoint}:`, error)
    }
    return { error: error }
  } finally {
    updatePending("db", -1)
  }
}

// Build a structured error when the server returns a non-JSON body (e.g. a
// ModSecurity/WAF block page, an Apache 5xx, or an HTML login redirect). The
// raw HTML body usually contains a rule ID or reference number — capture it
// so Sentry can show why the request was blocked.
function nonJsonResponseError(source: string, endpoint: string, response: Response, raw: string) {
  const status = response.status
  const preview = raw.replace(/\s+/g, " ").slice(0, 500)
  const wafRef = raw.match(/(?:reference\s*#?|transaction[_\s]*id[:\s]*|\bid\s*["']?)([0-9a-f.-]{6,})/i)?.[1] || null
  const looksLikeWaf = /mod_?security|not\s+acceptable|forbidden|blocked|firewall|reference\s*#/i.test(raw)
  const message = looksLikeWaf
    ? `Request blocked by the server firewall (HTTP ${status}). If you're on a VPN, try disabling it. Otherwise contact support.`
    : `Server returned a non-JSON response (HTTP ${status}). Please try again or contact support.`
  console.warn(`[${source}] Non-JSON response on ${endpoint}:`, { status, wafRef, preview })
  // Skip Sentry for VPN/ISP middlebox rejections: an upstream openresty proxy
  // returning 415 isn't our server and isn't actionable on our side.
  const isUpstreamProxy415 = status === 415 && !looksLikeWaf && /openresty/i.test(raw)
  if (!isUpstreamProxy415) {
    Sentry.captureMessage(`[${source}] Non-JSON response on ${endpoint}`, {
      level: "error",
      tags: { source, endpoint, status: String(status), waf: looksLikeWaf ? "likely" : "no", waf_ref: wafRef || "none" },
      extra: { status, wafRef, preview },
    })
  }
  return { error: message, status, wafRef, bodyPreview: preview }
}

export async function crowdinapi(endpoint: string, params: any = {}, offset: number = 0, abortSignal: AbortSignal | null = null) {
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  headers.set("X-CSRF-Token", localStorage.getItem("babble/csrf") || "")
  headers.set("X-SESSION-TOKEN", getSessionToken() || "")
  const requestOptions: RequestInit = { method: "POST", headers: headers, signal: abortSignal, credentials: "include" }
  const url = new URL(`${import.meta.env.VITE_API_URL}crowdinquery.php`)
  url.search = new URLSearchParams({ request: endpoint, offset: String(offset) }).toString()
  requestOptions.body = JSON.stringify(params)
  let response: Response | null = null
  let raw = ""
  try {
    updatePending("crowdin", 1)
    response = await fetch(url, requestOptions)

    const authError = await handleAuthError(response, "crowdinapi", endpoint, null)
    if (authError) return authError

    raw = await response.text()
    try {
      return JSON.parse(raw)
    } catch {
      return nonJsonResponseError("crowdinapi", endpoint, response, raw)
    }
  } catch (error) {
    console.warn("[crowdinapi] Fetch error:", error)
    return { error: error }
  } finally {
    updatePending("crowdin", -1)
  }
}

export async function paymentsapi(params = {}) {
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  const requestOptions: RequestInit = { method: "POST", headers: headers, credentials: "include", body: JSON.stringify(params) }
  const url = new URL(`${import.meta.env.VITE_API_URL}payments.php`)
  let response: Response | null = null
  let raw = ""
  try {
    response = await fetch(url, requestOptions)
    raw = await response.text()
    try {
      return JSON.parse(raw)
    } catch {
      return nonJsonResponseError("paymentsapi", "payments", response, raw)
    }
  } catch (error: any) {
    const status = response ? `HTTP ${response.status}` : "network error"
    console.warn("[paymentsapi] Network error:", status, error)
    return { error: `Payment server unreachable (${status}). Please try again or contact support.` }
  }
}

export async function authApi(request: string, params: Record<string, any> = {}) {
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  const url = new URL(`${import.meta.env.VITE_API_URL}auth.php`)
  url.searchParams.set("request", request)
  const requestOptions: RequestInit = {
    method: "POST",
    headers: headers,
    credentials: "include",
    body: JSON.stringify(params),
  }
  try {
    const response = await fetch(url, requestOptions)
    const answer = await response.json()
    return answer
  } catch (error) {
    console.log(`Auth API error on ${request}:`, error)
    return { error: error }
  }
}

export async function retry(fn: () => any, retries: number = 3, delay: number = 500): Promise<any> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === retries - 1) throw error // Fail after last attempt
      await new Promise((res) => setTimeout(res, delay * Math.pow(2, i))) // Exponential backoff
    }
  }
}

export async function sendEmail(
  emails: any,
  message: string,
  subject: string = "Message from Babble-on",
  user: string | null = localStorage.getItem("babble/pm"),
  saveSent: boolean = false,
  attachment_name: string | null = null,
  attachment_url: string | null = null,
  test: boolean = import.meta.env.VITE_TEST_EMAILS === "true",
  bcc: string[] = []
) {
  return await api("sendEmail", "POST", {
    emails: emails,
    message: message,
    subject: subject,
    user: user,
    saveSent: saveSent,
    attachment_name: attachment_name,
    attachment_url: attachment_url,
    test: test,
    bcc: bcc,
  })
}

// Usage info:
// -----
// Gets user level and info from the active session or PHP cookie. Saves to localStorage

export async function getUser(phpID?: string | null) {
  let session = await getSession()
  // Already verified this page load — return cached session immediately
  if (session && sessionVerifiedThisLoad) {
    return session
  }
  // Dedup: if a getUser verification is already in-flight, wait for it
  if (getUserPromise) return getUserPromise
  getUserPromise = _verifyAndGetUser(phpID, session).finally(() => {
    getUserPromise = null
  })
  return getUserPromise
}

async function _verifyAndGetUser(phpID?: string | null, session?: any) {
  phpID = phpID ?? getSessionToken() ?? getCookie("BABBLE_SID")
  console.log(`[getUser] verifying session, phpID=${phpID ? "present" : "missing"}, sessionVerified=${sessionVerifiedThisLoad}, cachedSession=${!!session}`)
  try {
    // If we have a session token, pass it explicitly. Otherwise, call
    // verifySession with no sessionId — auth.php falls back to the
    // httponly cookie via session_id(). This handles iOS Safari which
    // clears localStorage on tab close but keeps server-set cookies.
    const params = phpID && phpID !== "null" ? { sessionId: phpID } : {}
    const result = await authApi("verifySession", params)
    if (!result.valid) {
      authLog("getUser-invalid", { reason: result.reason, expired_ago: result.expired_ago, phpID: phpID ? "present" : "missing" })
      localStorage.removeItem("babble_session")
      localStorage.removeItem("babble/csrf")
      return false
    }
    session = { token: result.sessionId || phpID, data: result.user, impersonating: result.impersonating || false }
    localStorage.setItem("babble_session", JSON.stringify(session))
    localStorage.setItem("babble/csrf", result.user.csrf)
    sessionVerifiedThisLoad = true
    return session
  } catch (e) {
    console.warn("[getUser] verifySession threw:", e)
    return false
  }
}

// Usage info:
// -----
// Checks for and returns active logged-in session user

export function getSession() {
  try {
    const session = JSON.parse(localStorage.getItem("babble_session") || "null")
    if (session?.data) return session
  } catch {}
  return false
}

// Usage info:
// -----
// Checks user level against vue-router permission. Uses localStorage by default, checks server if expired or not there
export async function isUserAllowed(level: number[]) {
  let permission = false
  let user = await getUser()
  // doesn't exist, log in
  if (user == undefined || !user || !user.data?.user_level) {
    console.warn(`[isUserAllowed] No valid user, returning "login". user=${JSON.stringify(user ? "truthy" : user)}, user_level=${user?.data?.user_level}`)
    return "login"
  }
  for (var i in user.data.user_level) {
    if (level.includes(parseInt(user.data.user_level[i]))) permission = true
  }

  return permission
}

// Usage info:
// -----
// Used to get username info without setting a session

export async function getUserInfo(username: string) {
  return await api("getUserDetails", "POST", {
    username: username,
  })
}

// Usage info:
// -----
// Used to give the Job page a real <title>

export async function getJobTitle(id: any) {
  return await api("getJobTitle", "POST", {
    id: id,
  })
}

export async function getTranslatorList(client: string = "") {
  const translators = (await api("getTranslatorList", "POST", { client: client })) || []
  const languages = await getLanguageList(translators)
  return [translators, languages]
}

export async function getLanguageList(translators: any[]) {
  let languages: string[] = []
  for (var i = 0; i < translators.length; i++) {
    translators[i].languages = translators[i].language.replace(/-&gt;/g, "->").replace(/\//g, ", ").split(", ")
    translators[i].index = i
    languages.push(...translators[i].languages)
  }
  return [...new Set(languages)].sort()
}

// Usage info:
// -----
// Gets temporary URL from AWS

export async function getSafeURL(url: string) {
  return await api("getSafeURL", "POST", {
    url: url,
  })
}

// Usage info:
// -----
// Downloads from AWS

export async function download(url: string) {
  window.open(
    await api("getSafeURL", "POST", {
      url: url,
    }),
    "_blank"
  )
}

export const usdFormatter = new Intl.NumberFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
  style: "currency",
  currency: "USD",
})

// Total amount on a translator invoice, rounded to the cent the same way MySQL
// ROUND(...,2) does it (half-away-from-zero). Mirrors the SQL expression used
// in vue.php so display, payment, and accounting always agree to the penny —
// fractional-cent rates like $0.035/word would otherwise drift by $0.01 across
// the bank-vs-books reconciliation in CashBalance.
export function invoiceAmount(unitprice: number | string, totalunits: number | string, reviewprice: number | string = 0, reviewunits: number | string = 0): number {
  const raw = (parseFloat(unitprice as string) || 0) * (parseFloat(totalunits as string) || 0) + (parseFloat(reviewprice as string) || 0) * (parseFloat(reviewunits as string) || 0)
  return Math.round(raw * 100) / 100
}

// For per-word rates we need up to 3 decimals (e.g. $0.035). Translator invoice
// `unitprice`/`reviewprice` are stored at 3-decimal precision in the DB.
export const usdRateFormatter = new Intl.NumberFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
})

export function currencyFormatter(amount: number, currency: string) {
  const xchange = new Intl.NumberFormat(Intl.DateTimeFormat().resolvedOptions().locale, {
    style: "currency",
    currency: currency,
  })
  return xchange.format(amount)
}

export const numFormatter = new Intl.NumberFormat(Intl.DateTimeFormat().resolvedOptions().locale)

export const perCentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export async function queryCrowdin(query: any, operation: string | null = null, fields: any = null) {
  if (operation == "GET") operation = null
  const data = await crowdinapi("query", {
    query: query,
    operation: operation,
    fields: fields,
  })
  return data
}

export async function getFilesProgress(typeOf: "files" | "tasks" | "projects", crowdin: any, locales: any, signal: AbortSignal | null = null) {
  let method: Record<string, string> = { files: "wordcountbyfile", tasks: "wordcountbytask", projects: "wordsbyproject" }
  return await crowdinapi(method[typeOf], { crowdin: crowdin, languages: locales, byId: true }, 0, signal)
}

export function scrolledToBottom() {
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0

  return scrollTop + windowHeight >= documentHeight - 2
  // console.log(scrollTop, windowHeight, scrollTop + windowHeight, documentHeight)
}

export function waitForContent(id: string, callback: (html: string) => void, interval: number = 100) {
  const element = document.getElementById(id)
  if (element && element.innerHTML.trim()) {
    callback(element.innerHTML)
  } else {
    setTimeout(() => waitForContent(id, callback), interval)
  }
}

export async function replaceBranchwithFile(json: any) {
  return await crowdinapi("getBranchFiles", { project_id: json[0].api, branches: json[0].branches })
}

export function getFilenameParts(file: string) {
  let url = ""
  if (file.substring(0, 2) == "<a") {
    url = urldecode(file).split('href="')[1].split('"')[0]
    file = url.substring(url.lastIndexOf("/") + 1)
  } else if (/^https?:\/\//i.test(file)) {
    url = file
    file = urldecode(new URL(url).pathname.split("/").pop() || "")
  } else if (file.includes("/")) {
    file = urldecode(file)
    file = file.substring(file.lastIndexOf("/") + 1)
  }
  let dotIndex = file.lastIndexOf(".")
  let name_wo_ext = dotIndex > 0 ? file.substring(0, dotIndex) : file
  let ext = dotIndex > 0 ? file.substring(dotIndex + 1).toLowerCase() : ""
  let icon = "file"
  switch (ext) {
    case "xlsx":
      icon = "file-excel"
      break
    case "doc":
    case "docx":
      icon = "file-word"
      break
    case "ppt":
    case "pptx":
      icon = "file-powerpoint"
      break
    case "mp4":
    case "mp3":
      icon = "file-music"
      break
    case "xcstrings":
    case "strings":
    case "stringsdict":
      icon = "file-code"
      break
    case "jpeg":
    case "jpg":
    case "png":
    case "gif":
    case "svg":
    case "eps":
      icon = "file-image"
      break
    case "pdf":
      icon = "file-pdf"
      break
    case "zip":
      icon = "file-zipper"
      break
    case "csv":
      icon = "file-csv"
      break
    case "xml":
      icon = "file-code"
      break
  }
  return { name: file, name_wo_ext: name_wo_ext, ext: ext, icon: icon, url: url }
}

export function safefilename(f: string) {
  return f.replace(/[\/|\\|\:"\?|\*]/g, "").replace(/\s*\./, ".")
}
export function urldecode(str: string) {
  return decodeURIComponent((str + "").replace(/\+/g, "%20"))
}

export function isWithinLastXHours(date: Date | string | number, hours: number = 1) {
  const xHoursAgo = subHours(new Date(), hours)
  return isAfter(date, xHoursAgo)
}

// Usage info:
// -----
// import { useEventListener } from './utils'
//   useEventListener(window, 'mousemove', (event) => {
// 	x.value = event.pageX
// 	y.value = event.pageY
//   })

export function useEventListener(target: EventTarget, event: string, callback: EventListener) {
  // if you want, you can also make this
  // support selector strings as target
  onMounted(() => target.addEventListener(event, callback))
  onUnmounted(() => target.removeEventListener(event, callback))
}

// Usage info:
// import { getCookie } from './utils'
// const id = getCookie('name')

export function getCookie(id: string) {
  let value = document.cookie.match("(^|;)?" + id + "=([^;]*)(;|$)")
  return value ? unescape(value[2]) : null
}

export const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
}

export function isNewEmail(email: string, allEmails: string[]) {
  // checks if email is already in the list
  let emailalreadythere = allEmails.some((e: string) => {
    const rawemail = e.match(/<([^>]+)>/)
    if (rawemail && rawemail[1]) {
      return rawemail[1].toLowerCase() == email.toLowerCase()
    }
  })
  return !emailalreadythere
}

export function now(minutes: number = 0, dateFormat: string = "yyyy-MM-dd HH:mm:ss") {
  let date = new Date()
  if (minutes && minutes > 0) {
    date = new Date(date.getTime() + minutes * 60000)
  }
  return serverDate(date, dateFormat) // 2025-10-25 06:46:20
}

export function serverDate(datetime: Date | string | number, dateFormat: string = "yyyy-MM-dd HH:mm:ss") {
  return format(TZDate.tz("America/Los_Angeles", datetime as any), dateFormat) // 2025-10-25 06:46:20
}

export function inMyTimezone(givendate: Date | string | number | null | undefined, baseTimeZone: string = "America/Los_Angeles", timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone) {
  if (!givendate) return new Date(NaN)
  try {
    const d = transpose(new TZDate(givendate as any), tz(baseTimeZone)) as any
    return typeof d.withTimeZone === "function" ? d.withTimeZone(timeZone) : d
  } catch {
    return new Date(NaN)
  }
}

export function timeago(date: Date | string | number) {
  return formatDistanceToNow(inMyTimezone(date), { addSuffix: true })
}

export const formatSize = (bytes: number) => {
  var i = bytes == 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(+(bytes / Math.pow(1024, i)).toFixed(2) * 1) + " " + ["bytes", "k", "MB", "GB", "TB"][i]
}

export const debounce = (func: (...args: any[]) => any, delay: number = 600, immediate: boolean = false) => {
  let timeout: any
  return function (this: any, ...args: any[]) {
    const context = this
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, delay)
    if (callNow) func.apply(context, args)
  }
}

export function syntaxHighlight(json: string) {
  json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match: string) {
    var cls = "number"
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = "key"
      } else {
        cls = "string"
      }
    } else if (/true|false/.test(match)) {
      cls = "boolean"
    } else if (/null/.test(match)) {
      cls = "null"
    }
    return '<span class="' + cls + '">' + match + "</span>"
  })
}

export async function updateField(field: string, value: any, jobid: any, table: string = "projects") {
  return await api("updateField", "POST", {
    field: field,
    value: value,
    id: jobid,
    table: table,
  })
}

// For a Crowdin-linked job with no downloadlink yet, ask the backend to build
// the Crowdin project(s), download the finished translation zip, archive it to
// S3, and store the link in projects.downloadlink. The server no-ops if the job
// already has a downloadlink or isn't Crowdin-linked, so it's safe to call on
// every delivery. Building can take a while, so callers typically don't await.
export async function buildAndArchiveCrowdin(jobid: any) {
  return await api("buildAndArchiveCrowdin", "POST", { jobid: jobid })
}

export async function insertActivity(project: string = "", activity: string = "", jobid: any = "", language: string = "", user: string | null = localStorage.getItem("babble/pm")) {
  return await api("insertActivity", "POST", {
    project: project,
    activity: activity,
    jobid: jobid,
    language: language,
    user: user,
  })
}

export async function getTranslatorRatings(translators: any) {
  return await api("getRatings", "POST", {
    translators: translators,
  })
}

export async function getProjectBuilds(project_id: any, abortSignal: AbortSignal | null = null) {
  return await crowdinapi(
    "getProjectBuilds",
    {
      project_id: project_id,
    },
    0,
    abortSignal
  )
}

// Tell Crowdin to build the project now so a fresh export is available. Empty
// targetLanguageIds builds all languages. Returns { build: <new build data> }.
export async function buildProject(project_id: any, targetLanguageIds: string[] = [], abortSignal: AbortSignal | null = null) {
  return await crowdinapi(
    "buildProject",
    {
      project_id: project_id,
      targetLanguageIds: targetLanguageIds,
    },
    0,
    abortSignal
  )
}

export async function clearTempDirectory(name: string) {
  return await crowdinapi("clearTempDirectory", {
    name: name,
  })
}

export async function zipDirectory(name: string, path: string, deleteAfter: boolean = false) {
  return await api("zipDirectory", "POST", {
    name: name,
    path: path,
    delete: deleteAfter,
  })
}

export async function AWSupload(name: string, path: string, awsfolder: string, deleteAfter: boolean = false, bucket: string = "babbleon") {
  return await api("AWSupload", "POST", {
    name: name,
    path: path,
    folder: awsfolder,
    delete: deleteAfter,
    bucket: bucket,
  })
}

export async function exportCrowdinFiles(dir: string, project_id: any, dtype: string = "languages", codes: any = null, files: any = null, abortSignal: AbortSignal | null = null) {
  return await crowdinapi(
    "exportCrowdinFiles",
    {
      dir: dir,
      project_id: project_id,
      dtype: dtype,
      codes: codes,
      files: files,
    },
    0,
    abortSignal
  )
}

export async function clearNotifications(jobid: any, activity: any) {
  return await api("clearNotifications", "POST", {
    jobid: jobid,
    activity: activity,
  })
}

// Flatten a request/job project's `crowdin` field to the top-level props
// downstream consumers expect (crowdin slug, id, owner, api, domain). The new
// CrowdinRequestFlow stores it as an object { crowdin, id, owner, api, domain? };
// the legacy format used a positional [slug, id, owner, api, domain?] tuple.
// Mutates and returns the project in place. No-op for already-flat objects.
export function normalizeCrowdinField(j: any) {
  if (Array.isArray(j?.crowdin)) {
    const info = j.crowdin
    Object.assign(j, { crowdin: info[0], id: info[1], owner: info[2], api: info[3] })
    if (info[4]) j.domain = info[4]
  } else if (j?.crowdin && typeof j.crowdin === "object") {
    const info = j.crowdin
    Object.assign(j, { crowdin: info.crowdin, id: info.id, owner: info.owner, api: info.api })
    if (info.domain) j.domain = info.domain
  }
  return j
}

export function crowdinlink(typeOf: string, project: any, id: any, source: string, langcode: string, domain: string = "www") {
  source = source ? source.split("||")[0] : "en"
  switch (typeOf) {
    case "tasks":
      return `https://${domain}.crowdin.com/editor/${project}/all/${crowdinEditorCode(source)}-${crowdinEditorCode(langcode).toLowerCase()}?task=${id}`
    case "files":
      return `https://${domain}.crowdin.com/editor/${project}/${id}/${crowdinEditorCode(source)}-${crowdinEditorCode(langcode).toLowerCase()}`
    case "projects":
    default:
      return `https://${domain}.crowdin.com/project/${project}/${langcode}`
  }
}
export function crowdinEditorCode(code: string) {
  if (!code) return ""
  var fixlist = ["ar-sa", "en-us", "es-us", "en-gb", "es-mx", "fil", "fr-be", "fr-ca", "nl-be", "pt-br", "si-lk", "zh-cn", "zh-tw", "zh-hk", "zh-sg", "es-419"]
  if (fixlist.indexOf(code.toLowerCase()) >= 0) {
    return code.replace("-", "")
  } else return code.substring(0, 2)
}

// Crowdin returns es-419 for Spanish (Latin America) in some projects, but the app's canonical
// code is es-MX. Remap any es-419 keys on a Crowdin-stats object so downstream lookups by
// canonical code (e.g. translator.code, code2lang) resolve correctly.
export function normalizeCrowdinCodes<T extends Record<string, any>>(data: T): T {
  if (!data || (data as any).error) return data
  if (data["es-419"] && !data["es-MX"]) {
    ;(data as any)["es-MX"] = data["es-419"]
    delete (data as any)["es-419"]
  }
  return data
}

export function fixlocales(code: string, projectLocales: any) {
  if (!projectLocales) return code
  if (projectLocales[0]?.code) projectLocales = projectLocales.map((l: any) => l.code)

  if (code === "no" && projectLocales.includes("nb")) {
    code = "nb"
  } else if (code === "nb" && projectLocales.includes("no")) {
    code = "no"
  } else if (code === "en-us" && projectLocales.includes("en")) {
    code = "en-us"
  } else if (code === "es-MX" && projectLocales.includes("es-419") && !projectLocales.includes("es-MX")) {
    code = "es-419"
  } else if (code === "es-419" && projectLocales.includes("es-MX") && !projectLocales.includes("es-419")) {
    code = "es-MX"
  } else if (code === "sr" && projectLocales.includes("sr-CS")) {
    code = "sr"
  } else if (code === "sr-CS" && projectLocales.includes("sr")) {
    code = "sr-CS"
  } else if ((code === "es-MX" || code === "es-419") && projectLocales.includes("es-ES") && !projectLocales.includes("es-419") && !projectLocales.includes("es-MX")) {
    code = "es-ES"
  } else if ((code === "es-ES" || code === "es") && projectLocales.includes("es-419") && !projectLocales.includes("es-ES") && !projectLocales.includes("es-MX")) {
    code = "es-419"
  } else if ((code === "es-ES" || code === "es") && !projectLocales.includes("es-419") && !projectLocales.includes("es-ES") && projectLocales.includes("es-MX")) {
    code = "es-MX"
  } else if (code === "es-MX" && !projectLocales.includes("es-MX") && projectLocales.includes("es-419")) {
    code = "es-419"
  } else if (code === "es-419" && !projectLocales.includes("es-419") && projectLocales.includes("es-MX")) {
    code = "es-MX"
  } else if (code === "pt-PT" && projectLocales.includes("pt-BR") && !projectLocales.includes("pt-PT")) {
    code = "pt-BR"
  } else if (code === "pt-BR" && projectLocales.includes("pt-PT") && !projectLocales.includes("pt-BR")) {
    code = "pt-PT"
  } else if (code === "pt" && projectLocales.includes("pt-PT")) {
    code = "pt-PT"
  } else if (code === "es" && projectLocales.includes("es-ES")) {
    code = "es-ES"
  }

  return code
}

// converts invoice line item to the locale in description
export function getLineitemIcon(description: string) {
  const desc = (description || "").split(":")[0].trim()
  const patterns: [RegExp, string][] = [
    [/Review/i, "fas fa-eye"],
    [/(MTPE|Post Editing)/i, "fas fa-robot"],
    [/Transcreation/i, "fas fa-typewriter"],
    [/(Localization|Translation)/i, "fas fa-globe"],
    [/App.*description/i, "fab fa-app-store-ios"],
    [/Copywriting/i, "fal fa-edit"],
    [/cancel/i, "fal fa-times-circle"],
    [/Editing/i, "fas fa-cut"],
    [/Notary/i, "fas fa-certificate"],
    [/Document Translation/i, "fal fa-file-word"],
    [/Deposit/i, "fal fa-money-bill-alt"],
    [/Delivery/i, "fal fa-plane"],
    [/Discount/i, "fas fa-dollar-sign"],
    [/Phone/i, "fas fa-phone"],
    [/Release/i, "fas fa-newspaper"],
    [/Shipping/i, "fas fa-truck"],
    [/Consumables/i, "fal fa-gift-card"],
    [/Hourly/i, "fal fa-timer"],
    [/CDN/i, "fal fa-chart-network"],
    [/scan/i, "fal fa-scanner-image"],
    [/Meeting/i, "fas fa-phone"],
    [/Bank fee/i, "fas fa-piggy-bank"],
    [/Refund/i, "fas fa-sack-dollar"],
  ]
  for (const [regex, icon] of patterns) {
    if (regex.test(desc)) return icon
  }
  return "fal fa-globe"
}

export function getLanguageCodeFromLineitem(description: string, firstOrLast: number = 0) {
  const match = description.match(/:\s*([^<]*)/i)
  let lang = ""

  if (match && match[1]) {
    lang = match[1]
      .split(/ to /i)
      [firstOrLast] // split ignoring "to"/"To"/"TO"
      .trim()
      .toLowerCase()
  }
  if (!lang) {
    lang = description
      .split("->")
      [firstOrLast] // split trying ->
      .trim()
      .toLowerCase()
  }

  return langLookup[lang] || ""
}

export function imageExists(url: string) {
  return new Promise((resolve) => {
    const image = new Image()
    image.onload = () => resolve(true)
    image.onerror = () => resolve(false)
    image.src = url
  })
}

export async function getDeveloperImage(client: string) {
  let devs = await api("getDevelopers", "POST", { developers: [client] })
  if (devs[0]) return devs[0].clientimage
  return null
}

export async function askDropbox(command: string, options: any) {
  try {
    if (command === "filesUpload") {
      const fd = new FormData()
      fd.append("path", options.path)
      fd.append("file", options.contents)
      const url = new URL(`${import.meta.env.VITE_API_URL}vue.php`)
      url.search = new URLSearchParams({ request: "dropboxUpload" }).toString()
      const r = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": localStorage.getItem("babble/csrf") || "",
          "X-SESSION-TOKEN": getSessionToken() || "",
        },
        body: fd,
        credentials: "include",
      })
      return await r.json()
    }

    if (command === "filesDownload") {
      const link: any = await api("askDropbox", "POST", { command, options })
      if (link?.error) return link
      const r = await fetch(link.result.link)
      const fileBlob = await r.blob()
      return { result: { fileBlob } }
    }

    return await api("askDropbox", "POST", { command, options })
  } catch (error) {
    return { error: error }
  }
}

export async function getAWSlink(fileblob: Blob, filename: string) {
  if (!fileblob) return { error: "File blob is required" }
  if (!filename) return { error: "File name is required" }

  const contentType = fileblob.type || "application/octet-stream"

  const signed: any = await api("getS3UploadUrl", "POST", { filename, contentType })
  if (signed?.error) return { error: signed.error }

  try {
    const response = await fetch(signed.url, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
        "x-amz-server-side-encryption": "AES256",
        "x-amz-storage-class": "STANDARD_IA",
      },
      body: fileblob,
    })
    if (!response.ok) {
      const errorText = await response.text()
      return { error: `S3 upload failed: ${response.status} ${errorText}` }
    }
    return signed.publicUrl
  } catch (error: any) {
    return { error: `S3 upload failed: ${error.message}` }
  }
}

// export async function getAWSlink(fileblob, filename) {
// // Validate input parameters
// if (!fileblob) return { error: "File blob is required" }
// if (!filename) return { error: "File name is required" }
// const { S3Client, PutObjectCommand } = await import("@aws-sdk/client-s3")
// const bucketName = "babbleon"
// const s3 = new S3Client({ region: "us-west-1", credentials: { accessKeyId: import.meta.env.VITE_AWS_KEY, secretAccessKey: import.meta.env.VITE_AWS_SECRET } })
//
// const arrayBuffer = await fileblob.arrayBuffer()
//
// try {
//   const response = await s3.send(
//     new PutObjectCommand({
//       Bucket: bucketName,
//       Key: filename,
//       Body: arrayBuffer,
//       ContentType: fileblob.type || "application/octet-stream",
//       ServerSideEncryption: "AES256",
//       StorageClass: "STANDARD_IA",
//     })
//   )
//
//   const encodedKey = encodeURIComponent(filename).replace(/%20/g, "+").replace(/%2F/g, "/")
//   const publicUrl = `https://${bucketName}.s3.amazonaws.com/${encodedKey}`
//   return publicUrl
// } catch (s3Error) {
//   // Handle specific S3 errors
//   if (s3Error.name === "NoSuchBucket") {
//     return { error: `S3 bucket '${bucketName}' does not exist` }
//   } else if (s3Error.name === "AccessDenied") {
//     return { error: "Access denied: Check your AWS credentials and permissions" }
//   } else if (s3Error.name === "InvalidAccessKeyId") {
//     return { error: "Invalid AWS access key ID" }
//   } else if (s3Error.name === "SignatureDoesNotMatch") {
//     return { error: "Invalid AWS secret access key" }
//   } else {
//     return { error: `S3 upload failed: ${s3Error.message}` }
//   }
// }
// }

export function detectLanguage(invoicetext: string) {
  const patterns = [
    [/port.*eng.*/i, "Portuguese->English"],
    [/brazil/i, "Brazilian Portuguese"],
    [/(portugal|port.*euro)/i, "Portuguese (European)"],
    [/serbi.*lat/i, "Serbian (Latin)"],
    [/serbi/i, "Serbian"],
    [/spain/i, "Spanish (Spain)"],
    [/Spa.*(US|Uni)/, "Spanish (US)"],
    [/latin|mexico/i, "Spanish (Latin America)"],
    [/Canad/i, "French Canadian"],
    [/french.*eng.*/i, "French->English"],
    [/french/i, "French"],
    [/Hong|Kong|HK/i, "Chinese (HK-Traditional)"],
    [/trad/i, "Chinese (Traditional)"],
    [/simplified/i, "Chinese (Simplified)"],
    [/norw.*bok/i, "Norwegian Bokmal"],
    [/norw.*ny/i, "Norwegian Nynorsk"],
    [/norw/i, "Norwegian Bokmal"],
    [/cambod/i, "Khmer"],
    [/persian.*eng.*/i, "Farsi->English"],
    [/persian/i, "Farsi"],
    [/urdu/i, "Urdu"],
  ]

  for (const [regex, result] of patterns) {
    if ((regex as RegExp).test(invoicetext)) return result as string
  }

  return invoicetext
}

export function getCodeFromLanguages(languages: string) {
  if (/->/.test(languages)) {
    const splitLangs = languages.split("->")
    return `${lang2code[splitLangs[0]]}-${lang2code[splitLangs[1]]}`
  }
  return lang2code[languages]
}

// Canonical code→name mapping shared with PHP (public/php/languages.json)
import code2langMap from "@/data/languages.json"

// Reverse the canonical map to get name→code, plus add aliases for user-facing lookups
const _langAliases: Record<string, string> = {
  "Chinese Simplified": "zh-CN",
  "Chinese Traditional": "zh-TW",
  "Chinese (Simplified and Traditional)": "zh-ALL",
  "English, United Kingdom": "en-GB",
  Farsi: "fa",
  "French (France)": "fr",
  "French (Canada)": "fr-CA",
  "French (Canadian)": "fr-CA",
  Norwegian: "nb",
  Portuguese: "pt-PT",
  "Portuguese (Brazil)": "pt-BR",
  "Portuguese (Brazilian)": "pt-BR",
  "Portuguese (Brasil)": "pt-BR",
  "Portuguese (Brasilian)": "pt-BR",
  "Portuguese, Brazilian": "pt-BR",
  "Serbian (Latin)": "sr-CS",
  Spanish: "es-ES",
  "Spanish, Latin America": "es-MX",
  "Spanish, Puerto Rico": "es-PR",
  "Spanish, United States": "es-US",
  Urdu: "ur-IN",
}
export const lang2code: Record<string, string> = {
  // Canonical entries (reversed from code→name)
  ...Object.fromEntries(Object.entries(code2langMap).map(([code, name]) => [name, code])),
  // Aliases — alternative names that map to the same code
  ..._langAliases,
}

// build lowercase lookup
export const langLookup = Object.fromEntries(Object.entries(lang2code).map(([k, v]) => [k.toLowerCase(), v]))

// code→name from the shared JSON, plus ->English codes
export const code2lang: Record<string, string> = Object.fromEntries(
  Object.entries(code2langMap as Record<string, string>).flatMap(([code, lang]) => {
    const entries: [string, string][] = [
      [code, lang],
      [`${code}-en`, `${lang}->English`],
    ]
    // For regional codes like "es-ES", also add simplified "es-en" → "Spanish->English"
    if (code.includes("-")) {
      const base = code.split("-")[0]
      const baseLang = lang.replace(/ *\(.*\)$/, "").replace(/, .*$/, "")
      entries.push([`${base}-en`, `${baseLang}->English`])
    }
    return entries
  })
)

// Map a language code (e.g. "zh-CN", "ms", "ko") to the country code used by
// flag-icon CSS. Kept in sync with Flag.vue's `fixlist`.
const FLAG_FIX: Record<string, string> = {
  af: "za", ak: "gh", ar: "sa", am: "et", ca: "es", gl: "es", co: "fr", cnh: "mm", ha: "ne",
  cy: "gb", fy: "nl", gd: "gb", et: "ee", ko: "kr", bs: "ba", be: "by", sq: "al", cs: "cz",
  da: "dk", eo: "eu", eu: "es", fa: "ir", ga: "ie", fil: "ph", en: "us", he: "il", hi: "in",
  hmn: "la", ig: "ng", ja: "jp", ka: "ge", kk: "kz", km: "kh", ku: "sy", el: "gr", lb: "lu",
  lo: "la", ms: "my", mi: "nz", my: "mm", nb: "no", ps: "af", sm: "ws", rn: "bi", sn: "zw",
  sl: "si", su: "id", sw: "ke", tl: "ph", ti: "er", vi: "vn", yo: "bj", uk: "ua", "ur-IN": "pk",
  tg: "tj", ta: "lk", gu: "in", pa: "in", kn: "in", mr: "in", bn: "in", te: "in", ml: "in",
  sd: "in", yi: "il", xh: "zw", zh: "cn", "zh-Hans": "cn", "zh-Hant": "tw", zu: "za", pcm: "ng",
  "es-419": "mx", "es-US": "mx",
}
export function langToFlag(code: string): string {
  if (!code) return ""
  if (FLAG_FIX[code]) return FLAG_FIX[code]
  if (code.length === 2) return code.toLowerCase()
  const tail = code.slice(-2)
  return (FLAG_FIX[tail] || tail).toLowerCase()
}

export const MD5 = function (d: any): any {
  if (!d) return d
  var r = M(V(Y(X(d), 8 * d.length)))
  return r.toLowerCase()
}
function M(d: any): any {
  for (var _: any, m = "0123456789ABCDEF", f = "", r = 0; r < d.length; r++) (_ = d.charCodeAt(r)), (f += m.charAt((_ >>> 4) & 15) + m.charAt(15 & _))
  return f
}
function X(d: any): any {
  for (var _: any = Array(d.length >> 2), m = 0; m < _.length; m++) _[m] = 0
  for (m = 0; m < 8 * d.length; m += 8) _[m >> 5] |= (255 & d.charCodeAt(m / 8)) << m % 32
  return _
}
function V(d: any): any {
  for (var _ = "", m = 0; m < 32 * d.length; m += 8) _ += String.fromCharCode((d[m >> 5] >>> m % 32) & 255)
  return _
}
function Y(d: any, _: any): any {
  ;(d[_ >> 5] |= 128 << _ % 32), (d[14 + (((_ + 64) >>> 9) << 4)] = _)
  for (var m = 1732584193, f = -271733879, r = -1732584194, i = 271733878, n = 0; n < d.length; n += 16) {
    var h = m,
      t = f,
      g = r,
      e = i
    ;(f = md5_ii(
      (f = md5_ii(
        (f = md5_ii(
          (f = md5_ii(
            (f = md5_hh(
              (f = md5_hh(
                (f = md5_hh(
                  (f = md5_hh(
                    (f = md5_gg(
                      (f = md5_gg(
                        (f = md5_gg(
                          (f = md5_gg(
                            (f = md5_ff(
                              (f = md5_ff(
                                (f = md5_ff(
                                  (f = md5_ff(
                                    f,
                                    (r = md5_ff(
                                      r,
                                      (i = md5_ff(i, (m = md5_ff(m, f, r, i, d[n + 0], 7, -680876936)), f, r, d[n + 1], 12, -389564586)),
                                      m,
                                      f,
                                      d[n + 2],
                                      17,
                                      606105819
                                    )),
                                    i,
                                    m,
                                    d[n + 3],
                                    22,
                                    -1044525330
                                  )),
                                  (r = md5_ff(
                                    r,
                                    (i = md5_ff(i, (m = md5_ff(m, f, r, i, d[n + 4], 7, -176418897)), f, r, d[n + 5], 12, 1200080426)),
                                    m,
                                    f,
                                    d[n + 6],
                                    17,
                                    -1473231341
                                  )),
                                  i,
                                  m,
                                  d[n + 7],
                                  22,
                                  -45705983
                                )),
                                (r = md5_ff(r, (i = md5_ff(i, (m = md5_ff(m, f, r, i, d[n + 8], 7, 1770035416)), f, r, d[n + 9], 12, -1958414417)), m, f, d[n + 10], 17, -42063)),
                                i,
                                m,
                                d[n + 11],
                                22,
                                -1990404162
                              )),
                              (r = md5_ff(
                                r,
                                (i = md5_ff(i, (m = md5_ff(m, f, r, i, d[n + 12], 7, 1804603682)), f, r, d[n + 13], 12, -40341101)),
                                m,
                                f,
                                d[n + 14],
                                17,
                                -1502002290
                              )),
                              i,
                              m,
                              d[n + 15],
                              22,
                              1236535329
                            )),
                            (r = md5_gg(r, (i = md5_gg(i, (m = md5_gg(m, f, r, i, d[n + 1], 5, -165796510)), f, r, d[n + 6], 9, -1069501632)), m, f, d[n + 11], 14, 643717713)),
                            i,
                            m,
                            d[n + 0],
                            20,
                            -373897302
                          )),
                          (r = md5_gg(r, (i = md5_gg(i, (m = md5_gg(m, f, r, i, d[n + 5], 5, -701558691)), f, r, d[n + 10], 9, 38016083)), m, f, d[n + 15], 14, -660478335)),
                          i,
                          m,
                          d[n + 4],
                          20,
                          -405537848
                        )),
                        (r = md5_gg(r, (i = md5_gg(i, (m = md5_gg(m, f, r, i, d[n + 9], 5, 568446438)), f, r, d[n + 14], 9, -1019803690)), m, f, d[n + 3], 14, -187363961)),
                        i,
                        m,
                        d[n + 8],
                        20,
                        1163531501
                      )),
                      (r = md5_gg(r, (i = md5_gg(i, (m = md5_gg(m, f, r, i, d[n + 13], 5, -1444681467)), f, r, d[n + 2], 9, -51403784)), m, f, d[n + 7], 14, 1735328473)),
                      i,
                      m,
                      d[n + 12],
                      20,
                      -1926607734
                    )),
                    (r = md5_hh(r, (i = md5_hh(i, (m = md5_hh(m, f, r, i, d[n + 5], 4, -378558)), f, r, d[n + 8], 11, -2022574463)), m, f, d[n + 11], 16, 1839030562)),
                    i,
                    m,
                    d[n + 14],
                    23,
                    -35309556
                  )),
                  (r = md5_hh(r, (i = md5_hh(i, (m = md5_hh(m, f, r, i, d[n + 1], 4, -1530992060)), f, r, d[n + 4], 11, 1272893353)), m, f, d[n + 7], 16, -155497632)),
                  i,
                  m,
                  d[n + 10],
                  23,
                  -1094730640
                )),
                (r = md5_hh(r, (i = md5_hh(i, (m = md5_hh(m, f, r, i, d[n + 13], 4, 681279174)), f, r, d[n + 0], 11, -358537222)), m, f, d[n + 3], 16, -722521979)),
                i,
                m,
                d[n + 6],
                23,
                76029189
              )),
              (r = md5_hh(r, (i = md5_hh(i, (m = md5_hh(m, f, r, i, d[n + 9], 4, -640364487)), f, r, d[n + 12], 11, -421815835)), m, f, d[n + 15], 16, 530742520)),
              i,
              m,
              d[n + 2],
              23,
              -995338651
            )),
            (r = md5_ii(r, (i = md5_ii(i, (m = md5_ii(m, f, r, i, d[n + 0], 6, -198630844)), f, r, d[n + 7], 10, 1126891415)), m, f, d[n + 14], 15, -1416354905)),
            i,
            m,
            d[n + 5],
            21,
            -57434055
          )),
          (r = md5_ii(r, (i = md5_ii(i, (m = md5_ii(m, f, r, i, d[n + 12], 6, 1700485571)), f, r, d[n + 3], 10, -1894986606)), m, f, d[n + 10], 15, -1051523)),
          i,
          m,
          d[n + 1],
          21,
          -2054922799
        )),
        (r = md5_ii(r, (i = md5_ii(i, (m = md5_ii(m, f, r, i, d[n + 8], 6, 1873313359)), f, r, d[n + 15], 10, -30611744)), m, f, d[n + 6], 15, -1560198380)),
        i,
        m,
        d[n + 13],
        21,
        1309151649
      )),
      (r = md5_ii(r, (i = md5_ii(i, (m = md5_ii(m, f, r, i, d[n + 4], 6, -145523070)), f, r, d[n + 11], 10, -1120210379)), m, f, d[n + 2], 15, 718787259)),
      i,
      m,
      d[n + 9],
      21,
      -343485551
    )),
      (m = safe_add(m, h)),
      (f = safe_add(f, t)),
      (r = safe_add(r, g)),
      (i = safe_add(i, e))
  }
  return Array(m, f, r, i)
}
function md5_cmn(d: number, _: number, m: number, f: number, r: number, i: number) {
  return safe_add(bit_rol(safe_add(safe_add(_, d), safe_add(f, i)), r), m)
}
function md5_ff(d: number, _: number, m: number, f: number, r: number, i: number, n: number) {
  return md5_cmn((_ & m) | (~_ & f), d, _, r, i, n)
}
function md5_gg(d: number, _: number, m: number, f: number, r: number, i: number, n: number) {
  return md5_cmn((_ & f) | (m & ~f), d, _, r, i, n)
}
function md5_hh(d: number, _: number, m: number, f: number, r: number, i: number, n: number) {
  return md5_cmn(_ ^ m ^ f, d, _, r, i, n)
}
function md5_ii(d: number, _: number, m: number, f: number, r: number, i: number, n: number) {
  return md5_cmn(m ^ (_ | ~f), d, _, r, i, n)
}
function safe_add(d: number, _: number) {
  var m = (65535 & d) + (65535 & _)
  return (((d >> 16) + (_ >> 16) + (m >> 16)) << 16) | (65535 & m)
}
function bit_rol(d: number, _: number) {
  return (d << _) | (d >>> (32 - _))
}
